// src/components/contacts/ContactQuickActions.tsx
//
// Phase 3 — Reusable Call + SMS quick action buttons for a contact row.
// Calls now route through the browser VoIP device (useTwilioVoiceDevice)
// instead of the legacy server-bridge useInitiateCallMutation. SMS still
// uses the existing useSendSmsMutation since SMS has no client-side device.
//
// The from-number selector (CallModal) is no longer needed for VoIP calls
// because resolveOutboundFromNumber on the server picks the correct
// outbound number using the user's primary/circle/workspace assignment.
// Removing the modal also matches the spec requirement that contact
// actions use the SAME flow as the dialer on /phone.

import { useState } from 'react';
import { useSendSmsMutation } from '../../api/telephonyApi';
import { useTelephonyAvailability } from '../../hooks/useTelephonyAvailability';
import { useTwilioVoiceDevice } from '../../hooks/useTwilioVoiceDevice';

interface ContactQuickActionsProps {
  contactId: string;
  contactName: string;
  phone: string | null | undefined;
  /** Optional callback to bubble status messages up to the parent page */
  onStatus?: (banner: { type: 'success' | 'error'; message: string }) => void;
}

export default function ContactQuickActions({
  contactId,
  contactName,
  phone,
  onStatus,
}: ContactQuickActionsProps) {
  const {
    isPhoneAvailable,
    isSmsAvailable,
    canMakeCalls,
    canSendSms,
  } = useTelephonyAvailability();
  const {
    isReady: isVoiceReady,
    activeCall,
    connectToNumber,
  } = useTwilioVoiceDevice();
  const [sendSms, { isLoading: isSending }] = useSendSmsMutation();

  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsBody, setSmsBody] = useState('');

  // Per-action gating: Call requires phone.call + Phone surface + voice
  // device ready + no other call in progress. SMS requires sms.send + SMS
  // surface.
  //
  // STRICT TWILIO RULE: `isPhoneAvailable` is true ONLY when the workspace
  // has a connected Twilio integration AND at least one active Twilio
  // phone number — see useTelephonyAvailability. There is no other path
  // a button can become enabled.
  const callInProgress =
    activeCall.kind === 'connecting' || activeCall.kind === 'in_call';
  const callDisabled =
    !phone ||
    !isPhoneAvailable ||
    !canMakeCalls ||
    !isVoiceReady ||
    callInProgress;
  const smsDisabled = !phone || !isSmsAvailable || !canSendSms || isSending;

  // Spec-aligned error wording (matches server's TWILIO_NOT_CONFIGURED /
  // NO_PHONE_NUMBER message).
  const TWILIO_UNAVAILABLE_CALL =
    'Calling is unavailable. Twilio is not configured or no number is assigned.';
  const TWILIO_UNAVAILABLE_SMS =
    'SMS is unavailable. Twilio is not configured or no number is assigned.';

  const callDisabledReason = !phone
    ? 'Contact has no phone number'
    : !isPhoneAvailable
    ? TWILIO_UNAVAILABLE_CALL
    : !canMakeCalls
    ? 'You do not have permission to make calls'
    : !isVoiceReady
    ? 'Browser calling is not ready yet — open the Phone page to fix.'
    : callInProgress
    ? 'A call is already in progress'
    : '';
  const smsDisabledReason = !phone
    ? 'Contact has no phone number'
    : !isSmsAvailable
    ? TWILIO_UNAVAILABLE_SMS
    : !canSendSms
    ? 'You do not have permission to send SMS'
    : '';

  const errorMessage = (err: unknown, fallback: string, twilioFallback: string): string => {
    if (err && typeof err === 'object' && 'data' in err) {
      const d = (err as { data?: { code?: string; message?: string; error?: string } }).data;
      if (d?.code === 'TWILIO_NOT_CONFIGURED' || d?.code === 'NO_PHONE_NUMBER') {
        return twilioFallback;
      }
      return d?.message || d?.error || fallback;
    }
    return fallback;
  };

  const handleCall = async () => {
    if (!phone) return;
    try {
      // Quick-call surfaces intentionally omit the per-call `record` param;
      // the server falls through to workspace policy (workspace=always still
      // compliance-locks ON; workspace=off defaults OFF). See audit §Bypass Sites.
      await connectToNumber(phone, { contactId, contactName });
      onStatus?.({ type: 'success', message: `Calling ${contactName}` });
    } catch (err) {
      onStatus?.({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to start call.',
      });
    }
  };

  const handleSendSms = async () => {
    if (!phone || !smsBody.trim()) return;
    try {
      await sendSms({
        toNumber: phone,
        body: smsBody.trim(),
        contactId,
      }).unwrap();
      onStatus?.({ type: 'success', message: `SMS sent to ${contactName}` });
      setShowSmsModal(false);
      setSmsBody('');
    } catch (err) {
      onStatus?.({
        type: 'error',
        message: errorMessage(err, 'Failed to send SMS', TWILIO_UNAVAILABLE_SMS),
      });
    }
  };

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={handleCall}
        disabled={callDisabled}
        title={callDisabledReason || `Call ${contactName}`}
        aria-label={`Call ${contactName}`}
        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-50 text-green-600 hover:bg-green-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
          />
        </svg>
      </button>

      <button
        onClick={() => setShowSmsModal(true)}
        disabled={smsDisabled}
        title={smsDisabledReason || `SMS ${contactName}`}
        aria-label={`SMS ${contactName}`}
        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      </button>

      {/* SMS modal */}
      {showSmsModal && phone && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowSmsModal(false)}
        >
          <div
            className="bg-white rounded-lg max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-gray-900">SMS {contactName}</h3>
            <p className="text-xs text-gray-500 mt-1">{phone}</p>
            <textarea
              value={smsBody}
              onChange={(e) => setSmsBody(e.target.value)}
              placeholder="Type your message..."
              rows={4}
              maxLength={1600}
              className="mt-3 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              aria-label="SMS body"
              autoFocus
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-500">{smsBody.length}/1600</span>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowSmsModal(false);
                    setSmsBody('');
                  }}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendSms}
                  disabled={!smsBody.trim() || isSending}
                  className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSending ? 'Sending...' : 'Send SMS'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
